import { db } from '../config/firebase';
import { doc, getDoc, setDoc, collection, getDocs } from 'firebase/firestore';

const COLLECTION = 'crm_scripts';

// ── Caché en memoria para la sesión ──────────────────────────────────────────
const cache = {};

// ── Scripts por defecto ───────────────────────────────────────────────────────

export const DEFAULT_SCRIPTS = {
  sat: {
    id: 'sat',
    nombre: 'Avería SAT',
    descripcion: 'Script para atender llamadas de avería técnica',
    version: 2,
    activo: true,
    preguntas: [
      {
        id: 'sintoma',
        orden: 1,
        pregunta: '¿Qué problema reporta el cliente?',
        tipo: 'chips',
        multiple: true,
        campo: 'descripcionFallo',
        obligatorio: true,
        activo: true,
        generarDescripcion: true,
        opciones: [
          { id: 'no_enciende',     label: 'No enciende',          emoji: '🔋', texto: 'El equipo no enciende' },
          { id: 'no_enfria',       label: 'No enfría/calienta',   emoji: '🌡️', texto: 'El equipo no enfría ni calienta' },
          { id: 'ruidos',          label: 'Ruidos extraños',      emoji: '🔊', texto: 'Presenta ruidos extraños' },
          { id: 'fuga',            label: 'Fuga agua/gas',        emoji: '💧', texto: 'Hay fuga de agua o refrigerante' },
          { id: 'error_pantalla',  label: 'Error en pantalla',    emoji: '⚠️', texto: 'Muestra error en pantalla' },
          { id: 'mal_olor',        label: 'Mal olor',             emoji: '👃', texto: 'Despide mal olor' },
          { id: 'no_calienta_agua',label: 'No calienta agua',     emoji: '🚿', texto: 'No calienta el agua sanitaria' },
          { id: 'perdida_presion', label: 'Pérdida presión',      emoji: '📉', texto: 'Pierde presión' },
          { id: 'otro',            label: 'Otro...',              emoji: '✏️', esTextoLibre: true },
        ],
      },
      {
        id: 'desde_cuando',
        orden: 2,
        pregunta: '¿Desde cuándo ocurre?',
        tipo: 'quick-date',
        campo: 'fechaProblema',
        obligatorio: true,
        activo: true,
        opciones: [
          { id: 'hoy',          label: 'Hoy',                valor: 'today' },
          { id: 'ayer',         label: 'Ayer',               valor: 'yesterday' },
          { id: 'esta_semana',  label: 'Esta semana',        valor: 'this_week' },
          { id: 'mas_tiempo',   label: 'Hace más tiempo',    valor: 'older' },
          { id: 'exacta',       label: '📅 Fecha exacta',    valor: 'custom', esDatePicker: true },
        ],
      },
      {
        id: 'garantia',
        orden: 3,
        pregunta: '¿Está bajo garantía?',
        tipo: 'chips',
        multiple: false,
        campo: 'esGarantia',
        obligatorio: true,
        activo: true,
        condicional: { si: 'marca_garantia' },
        opciones: [
          { id: 'si', label: 'Sí, garantía',  emoji: '✅', valor: 'si' },
          { id: 'no', label: 'No, con coste', emoji: '💰', valor: 'no' },
        ],
      },
      {
        id: 'marca_garantia',
        orden: 4,
        pregunta: '¿Marca del equipo?',
        tipo: 'chips',
        multiple: false,
        campo: 'marcaGarantia',
        obligatorio: true,
        activo: true,
        mostrarSi: { preguntaId: 'garantia', valorId: 'si' },
        opciones: [
          { id: 'ariston', label: 'Ariston', emoji: '🔧', valor: 'ariston' },
          { id: 'svan',    label: 'Svan',    emoji: '🔧', valor: 'svan' },
          { id: 'nofer',   label: 'Nofer',   emoji: '🔧', valor: 'nofer' },
          { id: 'zapata',  label: 'Zapata',  emoji: '🔧', valor: 'zapata' },
          { id: 'otro',    label: 'Otro',    emoji: '❓', valor: 'otro' },
        ],
      },
      {
        id: 'tiene_factura',
        orden: 5,
        pregunta: '¿Tiene factura de compra?',
        tipo: 'opciones',
        campo: 'tieneFactura',
        obligatorio: true,
        activo: true,
        mostrarSi: { preguntaId: 'garantia', valorId: 'si' },
        opciones: [
          { id: 'si', label: 'Sí, tiene factura', emoji: '🧾', desc: 'Se puede tramitar la garantía',                                              valor: 'si' },
          { id: 'no', label: 'No tiene factura',  emoji: '⚠️', desc: 'Sin factura no se tramita la garantía — informar al cliente',               valor: 'no' },
        ],
      },
      {
        id: 'urgencia',
        orden: 6,
        pregunta: '¿Urgencia del caso?',
        tipo: 'opciones',
        campo: 'urgenciaProblema',
        obligatorio: true,
        activo: true,
        opciones: [
          { id: 'baja',    label: 'Sin urgencia',  emoji: '📅', desc: 'Puede esperar unos días',      valor: 'baja',    color: 'gray' },
          { id: 'media',   label: 'Media',         emoji: '⚠️', desc: 'Necesita solución pronto',     valor: 'media',   color: 'yellow' },
          { id: 'alta',    label: 'Urgente',        emoji: '🚨', desc: 'Necesita solución inmediata', valor: 'alta',    color: 'orange' },
          { id: 'critica', label: 'Crítico',        emoji: '⛔', desc: 'Sin servicio, emergencia',    valor: 'critica', color: 'red' },
        ],
      },
      {
        id: 'codigo_error',
        orden: 7,
        pregunta: 'Código de error (si aparece en pantalla)',
        tipo: 'texto',
        campo: 'codigoError',
        obligatorio: false,
        activo: true,
        placeholder: 'Ej: E01, F28, ERR-03...',
        multilinea: false,
      },
      {
        id: 'notas_tecnico',
        orden: 8,
        pregunta: 'Notas para el técnico',
        tipo: 'texto',
        campo: 'notasOperador',
        obligatorio: false,
        activo: true,
        placeholder: 'Instrucciones especiales, datos de acceso, horario del cliente...',
        multilinea: true,
      },
    ],
  },

  mantenimiento: {
    id: 'mantenimiento',
    nombre: 'Mantenimiento',
    descripcion: 'Script para solicitudes de mantenimiento preventivo',
    version: 3,
    activo: true,
    preguntas: [
      {
        id: 'contrato',
        orden: 1,
        pregunta: '¿Le interesaría un contrato de mantenimiento anual?',
        tipo: 'opciones',
        campo: 'interesadoContrato',
        obligatorio: true,
        activo: true,
        opciones: [
          { id: 'si', label: 'Sí, le interesa',           emoji: '✅', desc: 'Abrir formulario de contrato',         valor: 'si' },
          { id: 'no', label: 'Solo mantenimiento puntual', emoji: '🔧', desc: 'Continuar con el formulario habitual', valor: 'no' },
        ],
      },
      {
        id: 'tipo_equipo',
        orden: 2,
        pregunta: '¿Tipo de equipo?',
        tipo: 'chips',
        multiple: false,
        campo: 'tipoEquipoMantenimiento',
        obligatorio: true,
        activo: true,
        mostrarSi: { preguntaId: 'contrato', valorId: 'no' },
        opciones: [
          { id: 'split',               label: 'Split A/C',            emoji: '❄️', valor: 'split' },
          { id: 'conductos',           label: 'Conductos',            emoji: '🌀', valor: 'conductos' },
          { id: 'aerotermo',           label: 'Aerotermo (solo ACS)', emoji: '♨️', valor: 'aerotermo' },
          { id: 'aerotermo_split',     label: 'Aerotermo + Split',    emoji: '🔗', valor: 'aerotermo_split' },
          { id: 'aerotermo_conductos', label: 'Aerotermo + Conductos',emoji: '🔗', valor: 'aerotermo_conductos' },
          { id: 'termo',               label: 'Termo eléctrico',      emoji: '💧', valor: 'termo_electrico' },
          { id: 'caldera',             label: 'Caldera',              emoji: '🔥', valor: 'caldera' },
          { id: 'otro',                label: 'Otro',                 emoji: '🔧', valor: 'otro' },
        ],
      },
      {
        id: 'litraje_termo',
        orden: 2.5,
        pregunta: '¿Capacidad del termo?',
        tipo: 'chips',
        multiple: false,
        campo: 'litrajeTermoMTO',
        obligatorio: false,
        activo: true,
        mostrarSi: { preguntaId: 'tipo_equipo', valorId: 'termo' },
        opciones: [
          { id: 'hasta_110', label: '≤ 110 L',   emoji: '💧', valor: 'hasta_110' },
          { id: '150_200',   label: '150–200 L', emoji: '💧', valor: '150_200' },
          { id: '250_mas',   label: '≥ 250 L',   emoji: '💧', valor: '250_mas' },
        ],
      },
      {
        id: 'split_unidades',
        orden: 2.6,
        pregunta: '¿Cuántas unidades interiores?',
        tipo: 'chips',
        multiple: false,
        campo: 'splitUnidades',
        obligatorio: true,
        activo: true,
        mostrarSi: { preguntaId: 'tipo_equipo', valorIds: ['split', 'aerotermo_split'] },
        opciones: [
          { id: '1',   label: '1 unidad',   emoji: '1️⃣', valor: '1' },
          { id: '2',   label: '2 unidades', emoji: '2️⃣', valor: '2' },
          { id: '3',   label: '3 unidades', emoji: '3️⃣', valor: '3' },
          { id: 'mas', label: '+3 unidades', emoji: '➕', valor: 'mas' },
        ],
      },
      {
        id: 'split_exterior',
        orden: 2.7,
        pregunta: '¿Incluir mantenimiento de la unidad exterior?',
        tipo: 'opciones',
        campo: 'splitIncluirExterior',
        obligatorio: true,
        activo: true,
        mostrarSi: { preguntaId: 'tipo_equipo', valorIds: ['split', 'aerotermo_split'] },
        opciones: [
          { id: 'si', label: 'Sí, incluir exterior', emoji: '✅', desc: 'Interior + unidad exterior',  valor: 'si' },
          { id: 'no', label: 'Solo interior',         emoji: '🏠', desc: 'Solo unidades interiores',   valor: 'no' },
        ],
      },
      {
        id: 'ultimo_mantenimiento',
        orden: 3,
        pregunta: '¿Último mantenimiento realizado?',
        tipo: 'quick-date',
        campo: 'ultimoMantenimiento',
        obligatorio: true,
        activo: true,
        mostrarSi: { preguntaId: 'contrato', valorId: 'no' },
        opciones: [
          { id: 'nunca',       label: 'Nunca / No sabe', valor: 'never' },
          { id: 'este_anio',   label: 'Este año',        valor: 'this_year' },
          { id: 'anio_pasado', label: 'Año pasado',      valor: 'last_year' },
          { id: 'mas_2_anios', label: 'Hace +2 años',    valor: 'older' },
          { id: 'exacta',      label: '📅 Fecha exacta', valor: 'custom', esDatePicker: true },
        ],
      },
      {
        id: 'problemas',
        orden: 4,
        pregunta: '¿El cliente reporta algún problema?',
        tipo: 'chips',
        multiple: true,
        campo: 'problemasDetectados',
        obligatorio: false,
        activo: true,
        mostrarSi: { preguntaId: 'contrato', valorId: 'no' },
        generarDescripcion: true,
        opciones: [
          { id: 'sin_problemas', label: 'Sin problemas',    emoji: '✅', texto: 'Sin problemas reportados' },
          { id: 'rendimiento',   label: 'Bajo rendimiento', emoji: '📉', texto: 'Reporta bajo rendimiento' },
          { id: 'ruidos',        label: 'Ruidos',           emoji: '🔊', texto: 'Presenta ruidos' },
          { id: 'no_funciona',   label: 'No funciona bien', emoji: '❌', texto: 'El equipo no funciona correctamente' },
          { id: 'otro',          label: 'Otro...',          emoji: '✏️', esTextoLibre: true },
        ],
      },
      {
        id: 'observaciones',
        orden: 5,
        pregunta: 'Observaciones adicionales',
        tipo: 'texto',
        campo: 'observacionesMantenimiento',
        obligatorio: false,
        activo: true,
        mostrarSi: { preguntaId: 'contrato', valorId: 'no' },
        placeholder: 'Acceso al equipo, horario del cliente, condiciones especiales...',
        multilinea: true,
      },
      {
        id: 'notas_tecnico',
        orden: 6,
        pregunta: 'Notas para el técnico',
        tipo: 'texto',
        campo: 'notasOperador',
        obligatorio: false,
        activo: true,
        mostrarSi: { preguntaId: 'contrato', valorId: 'no' },
        placeholder: 'Instrucciones especiales para la visita...',
        multilinea: true,
      },
    ],
  },

  presupuesto: {
    id: 'presupuesto',
    nombre: 'Presupuesto',
    descripcion: 'Script de captación comercial para solicitudes de presupuesto',
    version: 1,
    activo: true,
    preguntas: [
      {
        id: 'servicio',
        orden: 1,
        pregunta: '¿Qué tipo de servicio solicita?',
        tipo: 'chips',
        multiple: false,
        campo: 'servicioPresupuesto',
        obligatorio: true,
        activo: true,
        opciones: [
          { id: 'aire_frio_calor',        label: 'Aire frío/calor',        emoji: '❄️', valor: 'aire_frio_calor' },
          { id: 'aerotermia',             label: 'Aerotermia',             emoji: '♨️', valor: 'aerotermia' },
          { id: 'calefaccion',            label: 'Calefacción',            emoji: '🔥', valor: 'calefaccion' },
          { id: 'acs',                    label: 'Agua caliente ACS',      emoji: '🚿', valor: 'acs' },
          { id: 'mantenimiento_contrato', label: 'Contrato mantenimiento', emoji: '📋', valor: 'mantenimiento_contrato' },
          { id: 'reparacion',             label: 'Reparación',             emoji: '🔧', valor: 'reparacion' },
        ],
      },
      {
        id: 'tipo_vivienda',
        orden: 2,
        pregunta: '¿Tipo de propiedad?',
        tipo: 'chips',
        multiple: false,
        campo: 'tipoViviendaPresupuesto',
        obligatorio: true,
        activo: true,
        opciones: [
          { id: 'piso',       label: 'Piso',         emoji: '🏢', valor: 'piso' },
          { id: 'casa',       label: 'Casa/Chalet',  emoji: '🏠', valor: 'casa' },
          { id: 'local',      label: 'Local/Oficina', emoji: '🏪', valor: 'local' },
          { id: 'obra_nueva', label: 'Obra nueva',   emoji: '🏗️', valor: 'obra_nueva' },
        ],
      },
      {
        id: 'metros',
        orden: 3,
        pregunta: '¿Metros cuadrados aproximados?',
        tipo: 'chips',
        multiple: false,
        campo: 'metrosPresupuesto',
        obligatorio: false,
        activo: true,
        opciones: [
          { id: 'menos_60',   label: '< 60 m²',     emoji: '📐', valor: 'menos_60' },
          { id: '60_90',      label: '60–90 m²',    emoji: '📐', valor: '60_90' },
          { id: '90_150',     label: '90–150 m²',   emoji: '📐', valor: '90_150' },
          { id: 'mas_150',    label: '> 150 m²',    emoji: '📐', valor: 'mas_150' },
        ],
      },
      {
        id: 'equipo_actual',
        orden: 4,
        pregunta: '¿Tiene equipo actualmente?',
        tipo: 'opciones',
        campo: 'equipoActualPresupuesto',
        obligatorio: true,
        activo: true,
        opciones: [
          { id: 'si_sustituir', label: 'Sí, a sustituir', emoji: '🔄', desc: 'Equipo existente que se reemplaza', valor: 'si_sustituir' },
          { id: 'si_conservar', label: 'Sí, lo conserva',  emoji: '✅', desc: 'Presupuesto solo de nuevo equipo',  valor: 'si_conservar' },
          { id: 'no',           label: 'No tiene',          emoji: '🆕', desc: 'Instalación desde cero',           valor: 'no' },
        ],
      },
      {
        id: 'decision_maker',
        orden: 5,
        pregunta: '¿El cliente toma la decisión él mismo?',
        tipo: 'opciones',
        campo: 'decisionMakerPresupuesto',
        obligatorio: false,
        activo: true,
        opciones: [
          { id: 'si',         label: 'Sí, decide él',      emoji: '✅', desc: 'Interlocutor directo',           valor: 'si' },
          { id: 'comunidad',  label: 'Comunidad/empresa',   emoji: '🏢', desc: 'Hay más personas implicadas',    valor: 'comunidad' },
          { id: 'pendiente',  label: 'Consulta con alguien',emoji: '🤔', desc: 'Necesita consultarlo',           valor: 'pendiente' },
        ],
      },
      {
        id: 'urgencia_presupuesto',
        orden: 6,
        pregunta: '¿Con qué urgencia necesita el presupuesto?',
        tipo: 'opciones',
        campo: 'urgenciaPresupuesto',
        obligatorio: true,
        activo: true,
        opciones: [
          { id: 'sin_prisa', label: 'Sin prisa', emoji: '📅', desc: 'Consultando opciones',  valor: 'sin-prisa' },
          { id: 'pronto',    label: 'Pronto',    emoji: '⏰', desc: 'En los próximos días',  valor: 'pronto' },
          { id: 'urgente',   label: 'Urgente',   emoji: '🚨', desc: 'Lo antes posible',       valor: 'urgente' },
        ],
      },
      {
        id: 'como_conocio',
        orden: 7,
        pregunta: '¿Cómo nos conoció?',
        tipo: 'chips',
        multiple: false,
        campo: 'comoConocioPresupuesto',
        obligatorio: false,
        activo: true,
        opciones: [
          { id: 'recomendacion', label: 'Recomendación',   emoji: '🤝', valor: 'recomendacion' },
          { id: 'google',        label: 'Google',          emoji: '🔍', valor: 'google' },
          { id: 'instagram',     label: 'Redes sociales',  emoji: '📱', valor: 'instagram' },
          { id: 'ya_cliente',    label: 'Ya es cliente',   emoji: '⭐', valor: 'ya_cliente' },
          { id: 'otro',          label: 'Otro',            emoji: '💬', valor: 'otro' },
        ],
      },
      {
        id: 'detalles',
        orden: 8,
        pregunta: 'Detalles adicionales del proyecto',
        tipo: 'texto',
        campo: 'detallesPresupuesto',
        obligatorio: false,
        activo: true,
        placeholder: 'Estancias a climatizar, preferencia de marca, plazo de ejecución, acceso a vivienda...',
        multilinea: true,
      },
      {
        id: 'notas_comercial',
        orden: 9,
        pregunta: 'Notas para el equipo comercial',
        tipo: 'texto',
        campo: 'notasOperador',
        obligatorio: false,
        activo: true,
        placeholder: 'Impresión del cliente, objeciones detectadas, puntos clave para la visita...',
        multilinea: true,
      },
    ],
  },

  instalacion: {
    id: 'instalacion',
    nombre: 'Instalación',
    descripcion: 'Script para solicitudes de instalación nueva',
    version: 1,
    activo: true,
    preguntas: [
      {
        id: 'tipo_instalacion',
        orden: 1,
        pregunta: '¿Qué tipo de instalación solicita?',
        tipo: 'chips',
        multiple: false,
        campo: 'tipoInstalacion',
        obligatorio: true,
        activo: true,
        opciones: [
          { id: 'split',       label: 'Split A/C',            emoji: '❄️', valor: 'split' },
          { id: 'conductos',   label: 'Climatización conductos',emoji: '🌀', valor: 'conductos' },
          { id: 'aerotermo',   label: 'Aerotermo',            emoji: '♨️', valor: 'aerotermo' },
          { id: 'caldera',     label: 'Caldera',              emoji: '🔥', valor: 'caldera' },
          { id: 'suelo_rad',   label: 'Suelo radiante',       emoji: '🌡️', valor: 'suelo_radiante' },
          { id: 'otro',        label: 'Otro',                 emoji: '🔧', valor: 'otro' },
        ],
      },
      {
        id: 'urgencia_instalacion',
        orden: 2,
        pregunta: '¿Urgencia de la instalación?',
        tipo: 'opciones',
        campo: 'urgenciaInstalacion',
        obligatorio: true,
        activo: true,
        opciones: [
          { id: 'sin_prisa', label: 'Sin prisa', emoji: '📅', desc: 'Planificando',          valor: 'sin_prisa' },
          { id: 'pronto',    label: 'Pronto',    emoji: '⏰', desc: 'Próximas semanas',      valor: 'pronto' },
          { id: 'urgente',   label: 'Urgente',   emoji: '🚨', desc: 'Lo antes posible',      valor: 'urgente' },
        ],
      },
      {
        id: 'descripcion_instalacion',
        orden: 3,
        pregunta: 'Descripción del proyecto',
        tipo: 'texto',
        campo: 'descripcionInstalacion',
        obligatorio: false,
        activo: true,
        placeholder: 'Superficie aproximada, número de estancias, estado de la vivienda...',
        multilinea: true,
      },
      {
        id: 'notas_tecnico',
        orden: 4,
        pregunta: 'Notas internas',
        tipo: 'texto',
        campo: 'notasOperador',
        obligatorio: false,
        activo: true,
        placeholder: 'Información adicional para el equipo técnico...',
        multilinea: true,
      },
    ],
  },
};

// ── Servicio ──────────────────────────────────────────────────────────────────

export const scriptsService = {
  async getScript(tipo) {
    if (cache[tipo]) return cache[tipo];
    try {
      const snap = await getDoc(doc(db, COLLECTION, tipo));
      if (snap.exists()) {
        const script = snap.data();
        const localDef = DEFAULT_SCRIPTS[tipo];
        // `personalizado` (ver saveScript) marca que un admin lo editó a
        // mano desde Scripts CRM — nunca se pisa con el default del código,
        // aunque el código traiga una versión más nueva. Sin esto, subir un
        // deploy que actualice DEFAULT_SCRIPTS[tipo].version borraba en
        // silencio cualquier personalización guardada con una versión
        // menor a la nueva del código.
        if (localDef && !script.personalizado && (localDef.version || 0) > (script.version || 0)) {
          await setDoc(doc(db, COLLECTION, tipo), localDef);
          cache[tipo] = localDef;
          return localDef;
        }
        cache[tipo] = script;
        return script;
      }
      // Si no existe en Firebase, sembrar el default y devolverlo
      const def = DEFAULT_SCRIPTS[tipo];
      if (def) {
        await setDoc(doc(db, COLLECTION, tipo), def);
        cache[tipo] = def;
        return def;
      }
      return null;
    } catch (e) {
      console.warn('[scriptsService] Firebase no disponible, usando default:', e.message);
      return DEFAULT_SCRIPTS[tipo] || null;
    }
  },

  async getAllScripts() {
    try {
      const snap = await getDocs(collection(db, COLLECTION));
      if (snap.empty) {
        // Sembrar todos los defaults
        await Promise.all(
          Object.values(DEFAULT_SCRIPTS).map(s =>
            setDoc(doc(db, COLLECTION, s.id), s)
          )
        );
        return { ...DEFAULT_SCRIPTS };
      }
      const scripts = {};
      snap.forEach(d => { scripts[d.id] = d.data(); });
      return scripts;
    } catch (e) {
      console.warn('[scriptsService] Firebase no disponible:', e.message);
      return { ...DEFAULT_SCRIPTS };
    }
  },

  async saveScript(tipo, script) {
    const toSave = {
      ...script,
      version: (script.version || 0) + 1,
      personalizado: true,
      actualizadoEn: new Date().toISOString(),
    };
    await setDoc(doc(db, COLLECTION, tipo), toSave);
    cache[tipo] = toSave;
    return toSave;
  },

  async resetToDefault(tipo) {
    const def = DEFAULT_SCRIPTS[tipo];
    if (!def) throw new Error(`No existe script por defecto para: ${tipo}`);
    // Vuelve a "pristino" — a partir de aquí, un default más nuevo del
    // código sí puede volver a actualizarlo automáticamente.
    const toSave = { ...def, version: 1, personalizado: false, actualizadoEn: new Date().toISOString() };
    await setDoc(doc(db, COLLECTION, tipo), toSave);
    cache[tipo] = toSave;
    return toSave;
  },

  clearCache(tipo) {
    if (tipo) delete cache[tipo];
    else Object.keys(cache).forEach(k => delete cache[k]);
  },
};
